import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DateTime } from 'luxon';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import IconCalendarDate from '../icons/IconCalendarDate';
import IconChevronDown from '../icons/IconChevronDown';
import language from '../../variables/language';

// Lesson.date is a plain ISO_LOCAL_DATE string, start/end are plain time
// strings - no parsing needed for those, just concatenation. The short
// weekday name (lang.weekDaysShort, same array LessonDetailModal's own
// weekday badge uses, index 0 = Monday) is prepended so two lessons a week
// apart aren't just two similar-looking dates.
const formatEventLabel = (event) => {
   const lesson = event.lesson;
   if (!lesson) return '#' + event.id;
   const time = lesson.start && lesson.end ? ` ${lesson.start}-${lesson.end}` : '';
   if (!lesson.date) return '';
   const dt = DateTime.fromFormat(lesson.date, 'yyyy-MM-dd');
   const weekday = dt.isValid ? language[language.lang].weekDaysShort[dt.weekday - 1] + ', ' : '';
   return weekday + lesson.date + time;
};

// Custom dropdown (portaled to document.body, same technique as
// TimezoneSelect.jsx) instead of a native <select> - options carry an icon.
// Only lessons that already have words attached are listable at all: the
// caller can then treat "a lesson is picked" as "a lesson with words is
// picked", no separate empty-lesson case to guard against.
export default function LessonWordSelect({ studentId, value, onChange, placeholder }) {
   const navigate = useNavigate();
   const lang = language[language.lang];
   const [events, setEvents] = useState([]);
   const [lessonIdsWithWords, setLessonIdsWithWords] = useState(null);
   const [open, setOpen] = useState(false);
   const [menuRect, setMenuRect] = useState(null);
   const rootRef = useRef(null);
   const menuRef = useRef(null);

   useEffect(() => {
      if (!studentId) { setEvents([]); setLessonIdsWithWords(null); return; }
      axios.get('/api/events', { params: { student: studentId }, withCredentials: true })
         .then((response) => setEvents(Array.isArray(response.data) ? response.data : []))
         .catch(() => setEvents([]));
      axios.get('/api/word-lessons/lesson-ids-with-words', { params: { studentId }, withCredentials: true })
         .then((response) => setLessonIdsWithWords(new Set(Array.isArray(response.data) ? response.data.map(Number) : [])))
         .catch((err) => {
            if (err.response && err.response.status === 401) navigate('/login');
            setLessonIdsWithWords(new Set());
         });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [studentId]);

   const options = useMemo(() => {
      if (!lessonIdsWithWords) return [];
      const seen = new Set();
      const list = [];
      events.forEach((ev) => {
         if (!ev.lesson || !lessonIdsWithWords.has(Number(ev.lesson.id))) return;
         if (seen.has(ev.lesson.id)) return;
         seen.add(ev.lesson.id);
         // Sort key stays the raw date+start (not the display label, which now
         // leads with a weekday name and would sort alphabetically by that
         // instead of chronologically).
         list.push({ id: ev.lesson.id, label: formatEventLabel(ev), sortKey: (ev.lesson.date || '') + (ev.lesson.start || '') });
      });
      return list.sort((a, b) => b.sortKey.localeCompare(a.sortKey));
   }, [events, lessonIdsWithWords]);

   const selected = options.find((o) => String(o.id) === String(value)) || null;

   const updateMenuRect = () => {
      if (!rootRef.current) return;
      const r = rootRef.current.getBoundingClientRect();
      setMenuRect({ top: r.bottom + 4, left: r.left, width: r.width });
   };

   useLayoutEffect(() => {
      if (!open) return;
      updateMenuRect();
      const onReposition = () => updateMenuRect();
      window.addEventListener('scroll', onReposition, true);
      window.addEventListener('resize', onReposition);
      return () => {
         window.removeEventListener('scroll', onReposition, true);
         window.removeEventListener('resize', onReposition);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [open]);

   useEffect(() => {
      const onClickOutside = (e) => {
         const inRoot = rootRef.current && rootRef.current.contains(e.target);
         const inMenu = menuRef.current && menuRef.current.contains(e.target);
         if (!inRoot && !inMenu) setOpen(false);
      };
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
   }, []);

   const choose = (option) => {
      onChange(option ? String(option.id) : '');
      setOpen(false);
   };

   const menu = open && menuRect ? createPortal(
      <div className='lesson-word-select-dropdown' ref={menuRef}
         style={{ position: 'fixed', top: menuRect.top, left: menuRect.left, width: menuRect.width }}>
         <div className={'lesson-word-select-option' + (!selected ? ' selected' : '')} onMouseDown={(e) => { e.preventDefault(); choose(null); }}>
            <span className='lesson-word-select-option-label'>{placeholder}</span>
         </div>
         {options.length === 0 ? (
            <div className='lesson-word-select-empty'>{lang['Empty list']}</div>
         ) : options.map((option) => (
            <div key={option.id} className={'lesson-word-select-option' + (selected && selected.id === option.id ? ' selected' : '')}
               onMouseDown={(e) => { e.preventDefault(); choose(option); }}>
               <IconCalendarDate style={{ width: 14, height: 14 }} />
               <span className='lesson-word-select-option-label'>{option.label}</span>
            </div>
         ))}
      </div>,
      document.body
   ) : null;

   return (
      <div className='lesson-word-select' ref={rootRef}>
         <div className='lesson-word-select-input' onClick={() => setOpen((v) => !v)}>
            {selected ? (
               <>
                  <IconCalendarDate style={{ width: 14, height: 14 }} />
                  <span className='lesson-word-select-option-label'>{selected.label}</span>
               </>
            ) : (
               <span className='lesson-word-select-placeholder'>{placeholder}</span>
            )}
            <IconChevronDown style={{ width: 12, height: 12, marginLeft: 'auto' }} />
         </div>
         {menu}
      </div>
   );
}
